import React, { useState } from 'react';
import {
  ClockCircleOutlined,
  RobotOutlined,
  CalendarOutlined,
  NotificationOutlined,
  RightOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useResponsive } from '../../hooks/useResponsive';
import { useGetBusinessRulesQuery } from '../../services/validationApi';
import { useGetChangelogUnseenCountQuery } from '../../services/changelogApi';
import AiDailySummaryModal, { useAiDailySummaryStatus } from './AiDailySummaryModal';
import DailyMenuModal, { useDailyMenuStatus } from './DailyMenuModal';
import './dashboardStrip.css';

type StripModal = 'ai' | 'menu' | null;

type OpenState = 'open' | 'closed' | 'unknown' | 'hidden';

/**
 * Restaurant open/closed, with the old card's grace period: if the rules take
 * more than 5s we stop waiting and say so rather than leaving a hole in the strip.
 */
function useRestaurantOpenState(): OpenState {
  const { data: businessRules, isLoading, error } = useGetBusinessRulesQuery();
  const [timedOut, setTimedOut] = useState(false);

  React.useEffect(() => {
    if (!isLoading) return;
    const timer = setTimeout(() => setTimedOut(true), 5000);
    return () => clearTimeout(timer);
  }, [isLoading]);

  if (error) return 'unknown';
  // Data wins over the timeout: a load slower than the grace period still
  // resolves to the real state once it lands, instead of leaving the pill
  // stuck on "no disponible" until the next mount.
  if (businessRules) return businessRules.isWithinOperatingHours ? 'open' : 'closed';
  if (timedOut) return 'unknown';
  return 'hidden';
}

const OPEN_LABEL: Record<string, string> = {
  open: 'ABIERTO',
  closed: 'CERRADO',
  unknown: 'ESTADO NO DISPONIBLE',
};

/**
 * One bar replacing what used to be three stacked cards (restaurant status, AI
 * digest, daily menu) plus a home for the Novedades badge. Each pill carries the
 * one number worth glancing at and opens its detail on demand; every pill hides
 * itself when it has nothing to say, so a quiet day leaves a quiet strip.
 */
const DashboardStatusStrip: React.FC = () => {
  const { isMobile } = useResponsive();
  const navigate = useNavigate();
  const [openModal, setOpenModal] = useState<StripModal>(null);

  const openState = useRestaurantOpenState();
  const ai = useAiDailySummaryStatus();
  const menu = useDailyMenuStatus();

  // MainLayout already subscribes to this for the sidebar badge (same roles
  // that can reach the dashboard), so this reuses its cache — no extra request.
  const { data: changelogUnseen } = useGetChangelogUnseenCountQuery();
  const unseenCount = changelogUnseen?.count ?? 0;

  const showStatus = openState !== 'hidden';
  if (!showStatus && !ai.visible && !menu.visible && unseenCount === 0) {
    return null;
  }

  return (
    <>
      <div className={`dash-strip${isMobile ? ' dash-strip--mobile' : ''}`}>
        {showStatus && (
          <span className="dash-pill dash-pill--static">
            <span
              className={`dash-pill__dot${
                openState === 'open' ? ' dash-pill__dot--open' : openState === 'closed' ? ' dash-pill__dot--closed' : ''
              }`}
            />
            <ClockCircleOutlined className="dash-pill__icon" />
            {OPEN_LABEL[openState]}
          </span>
        )}

        {ai.visible && (
          <button
            type="button"
            className="dash-pill"
            onClick={() => setOpenModal('ai')}
            aria-label={`Resumen IA${ai.alertCount > 0 ? `, ${ai.alertCount} alertas` : ''}`}
          >
            <RobotOutlined className="dash-pill__icon" />
            Resumen IA
            {ai.alertCount > 0 && (
              <span className="dash-pill__badge dash-pill__badge--warning">{ai.alertCount}</span>
            )}
            <RightOutlined className="dash-pill__chevron" />
          </button>
        )}

        {menu.visible && (
          <button
            type="button"
            className={`dash-pill${menu.configured ? '' : ' dash-pill--attention'}`}
            onClick={() => setOpenModal('menu')}
          >
            <CalendarOutlined className="dash-pill__icon" />
            {menu.configured ? 'Menú del día' : 'Menú del día pendiente'}
            {menu.configured && (
              <>
                <span className="dash-pill__sep">·</span>
                <span className="dash-pill__count">{menu.totalDiners} comensales</span>
              </>
            )}
            <RightOutlined className="dash-pill__chevron" />
          </button>
        )}

        {unseenCount > 0 && (
          <button
            type="button"
            className="dash-pill dash-pill--news"
            onClick={() => navigate('/novedades')}
            aria-label={`Novedades, ${unseenCount} sin leer`}
          >
            <NotificationOutlined className="dash-pill__icon" />
            Novedades
            <span className="dash-pill__badge dash-pill__badge--news">{unseenCount}</span>
            <RightOutlined className="dash-pill__chevron" />
          </button>
        )}
      </div>

      <AiDailySummaryModal open={openModal === 'ai'} onClose={() => setOpenModal(null)} />
      <DailyMenuModal open={openModal === 'menu'} onClose={() => setOpenModal(null)} />
    </>
  );
};

export default DashboardStatusStrip;
